<i18n src="@/locales/modules/UserSettingsTranslate.json"></i18n>

<template>
	<vx-card no-shadow class="contained-example-container">
		<div id="div-with-loading">
			<!-- Languages -->
			<div class="mt-0">
				<label class="text-sm">{{ $t("Languages") }}:</label>
				<v-select :reduce="timezone => timezone.key" label="value" cl v-model="settings.locale" :options="$store.state.auth.locales" :dir="$vs.rtl ? 'rtl' : 'ltr'">
					<template v-slot:option="option">
						{{ option.value | capitalize }}
					</template>
					<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
				</v-select>
			</div>

			<!-- Timezone -->
			<div class="mt-8">
				<label class="text-sm">{{ $t("Timezone") }}:</label>
				<v-select :reduce="timezone => timezone.key" label="value" v-model="settings.timezone" :options="$store.state.auth.timezones" :dir="$vs.rtl ? 'rtl' : 'ltr'">
					<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
				</v-select>
			</div>

			<!-- Date time format -->
			<div class="mt-8">
				<label class="text-sm">{{ $t("DatetimeFormat") }}:</label>
				<v-select :reduce="DatetimeFormat => DatetimeFormat.key" label="value" v-model="settings.datetime_format" :options="$store.state.auth.datetimes" :dir="$vs.rtl ? 'rtl' : 'ltr'">
					<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
				</v-select>
			</div>

			<div class="mt-8" v-if="false">
				<label class="text-sm">{{ $t("TodoSpeechDelay") }}:</label>
				<cbs-input
					v-model="settings.todo_speech_delay"
					class="w-full"
					type="number"
					dataType="int"
					min="1"
					name="todo_speech_delay"
					v-validate="{
						min_value: 1,
						required: true
					}"
				/>
			</div>

			<div class="mt-8" v-if="dashboardItemsWithPermission && dashboardItemsWithPermission.length > 0">
				<label class="text-sm">{{ $t("DashboardConfig") }}:</label>
				<div class="mt-2 flex flex-wrap justify-between">
					<vs-checkbox v-for="(dItem, index) in dashboardItemsWithPermission" :key="index" v-model="dItem.value">{{ $t(`DashboardConfigCheckBoxLabel.${dItem.name}`) }}</vs-checkbox>
				</div>
			</div>

			<!-- Save & Reset Button -->
			<div class="flex flex-wrap items-center sm:justify-end">
				<vs-button class="w-full sm:w-1/3 sm:max-w-xs mt-4 font-bold" @click="saveChanges">{{ $t("Actions.SaveChanges") }}</vs-button>
			</div>
		</div>
	</vx-card>
</template>

<script>
import * as _ from "lodash";
import i18n from "@/i18n.js";
import vSelect from "vue-select";
import { getUserPermissions, devicePermissions, requestPermissions, todoPermissions } from "@/mixins/mixins.js";

export default {
	mixins: [getUserPermissions({ ...devicePermissions, ...requestPermissions, ...todoPermissions })],
	components: {
		vSelect
	},
	data() {
		return {
			settings: {
				datetime_format: "",
				locale: "",
				timezone: "",
				todo_speech_delay: 60
			},
			dashboard_items: [
				{ key: "list_requests", value: true, name: "ListRequests", isHasPermission: false },
				{ key: "list_devices", value: true, name: "ListDevices", isHasPermission: false },
				{ key: "list_device_alerts", value: true, name: "ListDeviceAlerts", isHasPermission: false },
				{ key: "list_todo_statistics", value: true, name: "ListTodoStatistics", isHasPermission: false },
				{ key: "list_todo_assigned_to_me", value: true, name: "ListTodoAssignedToMe", isHasPermission: false }
			]
		};
	},
	mounted() {
		this.getConfigData();
	},
	methods: {
		async getConfigData() {
			this.showLoading();
			await this.$store.dispatch("auth/fetchUserSettings");
			await this.$store.dispatch("auth/fetchTimezones");
			await this.$store.dispatch("auth/fetchLocales");
			await this.$store.dispatch("auth/fetchDatetimeFormat");

			this.settings = Object.assign(this.settings, this.$store.state.auth.user_settings);
			const dashboard_items = this.$store.state.auth.user_settings.dashboard_items;
			this.renderDataCheckboxDashboard(dashboard_items);
			this.hideLoading();
		},
		renderDataCheckboxDashboard(dashboard_items) {
			this.dashboard_items.map((item, index) => {
				const idx = _.indexOf(dashboard_items, item.key);
				if (idx !== -1) {
					this.dashboard_items[index].value = true;
				} else {
					this.dashboard_items[index].value = false;
				}
				if (this.dashboard_items[index].key === "list_requests") {
					this.dashboard_items[index].isHasPermission = this.mixinIsCanViewRequest;
				}
				if (this.dashboard_items[index].key === "list_devices" || this.dashboard_items[index].key === "list_device_alerts") {
					this.dashboard_items[index].isHasPermission = this.mixinIsCanViewDevice;
				}
				if (this.dashboard_items[index].key === "list_todo_statistics" || this.dashboard_items[index].key === "list_todo_assigned_to_me" ) {
					this.dashboard_items[index].isHasPermission = this.mixinIsCanViewTodo;
				}
			});
		},
		updateLocale(locale) {
			if (locale) {
				i18n.locale = locale === "vn" ? "vi" : locale;
				this.$validator.localize(locale === "vn" ? "vi" : locale);
			}
		},
		saveChanges() {
			let _dashboardItems = [];
			this.dashboard_items.map(item => {
				if (item.value) _dashboardItems.push(item.key);
			});
			this.settings.dashboard_items = _dashboardItems;
			this.showLoading();
			this.$store
				.dispatch("auth/updateUserSettings", { settings: JSON.stringify(this.settings) })
				.then(res => {
					this.hideLoading();
					this.updateLocale(res.locale);
					this.$nextTick(() => {
						this.$helper.notifySuccess({
							context: this,
							options: {
								text: this.$t("UpdateConfigSuccessful")
							}
						});
					});
				})
				.catch(error => {
					this.$helper.notifyError({ context: this, error });
				});
		},
		showLoading() {
			this.$vs.loading({
				container: "#div-with-loading",
				scale: 0.6
			});
		},
		hideLoading() {
			this.$vs.loading.close("#div-with-loading > .con-vs-loading");
		}
	},
	computed: {
		dashboardItemsWithPermission() {
			return this.dashboard_items.filter(item => item.isHasPermission);
		}
	}
};
</script>
